import _ from 'lodash';

import template from './sir-trevor.html';
import { createHtmlBlock } from './html';
import { addToast } from '../../../modules/core/actions';
import {
  ToastType,
  ROOM_WIDGET_IMAGE_SIZE_UPLOAD_LIMIT,
  ROOM_WIDGET_IMAGES_COUNT_LIMIT,
} from '../../../modules/core/constants';
import { localeService } from '../../../modules/core';

const blockEvents = [
  'block:remove',
  'block:changePosition',
  'block:rerender',
  'block:replace',
];

export default angular
  .module('doxy.common.components.sirTrevor', [])
  .factory('contentEditorDataFactory', function ($rootScope, $ngRedux) {
    let editor = {};
    let initialWidgetsState;

    function set(editorInstance) {
      editor = editorInstance;
      blockEvents.forEach((event) => {
        const handler =
          event === 'block:remove'
            ? function () {
                editor.onFormSubmit();
              }
            : function () {
                $rootScope.$apply();
              };
        editor.mediator.on(event, handler);
      });

      editor.mediator.on('block:countUpdate', () => {
        const skipOnFormSubmit = true;
        const widgets = this.get(skipOnFormSubmit);
        const imageWidgets =
          widgets && widgets.filter((widget) => widget.type == 'image');
        if (
          imageWidgets &&
          imageWidgets.length >= ROOM_WIDGET_IMAGES_COUNT_LIMIT
        ) {
          const message = localeService.formatMessageById(
            'notification.editWaitingRoom.image-widgets-limit'
          );
          $ngRedux.dispatch(addToast(ToastType.warning, message));
        }
        $rootScope.$apply();
      });

      reinitWidgets();
    }

    function parseServerData(room) {
      const order = room.widgetsOrder ? room.widgetsOrder.split(',') : [];
      room.widgets = room.widgets || [];
      room.widgets.sort(function (a, b) {
        return order.indexOf(String(a.id)) - order.indexOf(String(b.id));
      });

      const widgets = room.widgets.map(function (widget) {
        const file = widget.imageUrl ? { url: widget.imageUrl } : null;
        return {
          type: widget.type,
          id: widget.id,
          data: {
            id: widget.id,
            text: widget.text,
            source: widget.videoSource,
            remote_id: widget.videoRemoteId,
            file: file,
          },
        };
      });

      return {
        data: widgets,
      };
    }

    function _parseWidget(widget) {
      const data = {
        type: widget.type,
        id: widget.data.id,
      };
      switch (widget.type) {
        case 'html':
          data.text = widget.data.text;
          break;
        case 'text':
          data.text = widget.data.text;
          break;
        case 'image':
          data.imageUrl = widget.data.file ? widget.data.file.url : null;
          break;
        case 'video':
          data.videoSource = widget.data.source;
          data.videoRemoteId = widget.data.remote_id;
          break;
      }
      return data;
    }

    function _parseData(raw) {
      return raw.data
        .map(function (widget) {
          if (!Object.keys(widget.data).length) {
            return;
          }

          return _parseWidget(widget);
        })
        .filter(function (elem) {
          return !!elem;
        });
    }

    function get(skipOnFormSubmit = false) {
      if (!skipOnFormSubmit) {
        if (editor.onFormSubmit()) {
          return;
        }
      }

      const raw = editor.store.retrieve();
      return _parseData(raw);
    }

    function isDirty() {
      if (!editor.block_manager) return false;

      const widgets = editor.$wrapper
        .find('.st-block')
        .map(function (_, block) {
          const _block = editor.block_manager.findBlockById(block.id);
          return _block ? _parseWidget(_block.getData()) : null;
        })
        .toArray();

      return !_.isEqual(widgets, initialWidgetsState);
    }

    function reinitWidgets(widgets) {
      initialWidgetsState = widgets || _parseData(editor.store.retrieve());
    }

    return {
      set,
      get,
      parseServerData,
      isDirty,
      reinitWidgets,
    };
  })
  .factory(
    'sirTrevorFactory',
    function ($window, LoopBackAuth, apiUrl, s3config, $rootScope, $ngRedux) {
      function interceptUpload($) {
        $.ajaxPrefilter(function (options) {
          if (!options.url.match('/upload')) {
            return;
          }

          options.headers = {
            authorization: LoopBackAuth.accessTokenId,
          };

          options.error = (jqXHR) => {
            const maxFileSizeExceededServerMessage = 'maxFileSize exceeded';
            let message = localeService.formatMessageById(
              'notification.editWaitingRoom.image-upload-generic-error'
            );
            if (
              jqXHR.status === 500 &&
              jqXHR.responseText &&
              jqXHR.responseText.includes(maxFileSizeExceededServerMessage)
            ) {
              message = localeService.formatMessageById(
                'notification.editWaitingRoom.image-size-limit-error',
                { limit: ROOM_WIDGET_IMAGE_SIZE_UPLOAD_LIMIT }
              );
            }
            $ngRedux.dispatch(addToast(ToastType.error, message));
          };
        });
      }

      function get() {
        return import('jquery').then(({ default: $ }) => {
          $window.$ = $;
          return import('sir-trevor-custom').then(({ default: SirTrevor }) => {
            $window.SirTrevor = SirTrevor;

            createHtmlBlock(SirTrevor);
            SirTrevor.setBlockOptions('Text', {
              _onBlur: function () {
                $rootScope.$apply();
              },
            });

            SirTrevor.setDefaults({
              uploadUrl: `${apiUrl}/api/Storages/${s3config.waitingRoom}/upload?imageType=roomWidget`,
            });
            interceptUpload($);

            return { $, SirTrevor };
          });
        });
      }

      return {
        get: get,
      };
    }
  )
  .directive('dSirTrevor', [
    'sirTrevorFactory',
    'contentEditorDataFactory',
    '$translate',
    function (sirTrevorFactory, contentEditorDataFactory, $translate) {
      return {
        template,
        scope: {
          disabled: '=dDisabled',
          data: '=dData',
        },
        restrict: 'EA',
        link: function (scope, element) {
          if (scope.data && scope.data.widgetsOrder) {
            scope.data = contentEditorDataFactory.parseServerData(scope.data);
          }

          sirTrevorFactory.get().then(function (w) {
            w.SirTrevor.LANGUAGE = 'en';
            w.SirTrevor.initialLoad = true;
            const $form = w.$(element);
            const $textArea = $form.find('textarea');
            const editor = new w.SirTrevor.Editor({
              el: $textArea,
              blockTypes: ['Text', 'Image', 'Video', 'Html'],
              onEditorRender: function () {
                w.SirTrevor.initialLoad = false;
                if (scope.disabled) {
                  $form.find('[contenteditable]').removeAttr('contenteditable');
                }
                $form
                  .find('[data-type="text"]')
                  .contents()
                  .last()[0].textContent = $translate.instant(
                  'account.waiting-room.edit.type.text'
                );
                $form
                  .find('[data-type="image"]')
                  .contents()
                  .last()[0].textContent = $translate.instant(
                  'account.waiting-room.edit.type.image'
                );
                $form
                  .find('[data-type="video"]')
                  .contents()
                  .last()[0].textContent = $translate.instant(
                  'account.waiting-room.edit.type.video'
                );
              },
            });
            contentEditorDataFactory.set(editor);
          });
        },
      };
    },
  ]).name;
